import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import AuthContext from '../../context/authContext';
import useIssue from '../../hooks/useIssue';
import { ISSUE_PRIORITY_BY_KEY } from '../../util/issueConstants';

const DATE_FORMAT = 'DD.MM.YYYY HH:mm';

// Severity ranking of the traffic light: higher wins when statuses are combined.
const SEVERITY_RANK = { green: 0, blue: 1, yellow: 2, red: 3 };

// Collect all resource ids of an entertainment (plain list + ordered resources), deduplicated.
const entertainmentResourceIds = e => [
  ...new Set([
    ...(e.resourceIds || []),
    ...((e.orderedResources?.resources || []).map(r => r.resourceId) || []),
  ]),
];

const branchLabel = branch => branch?.longName || branch?.locationName || '';

const isOpenIssue = issue => issue.status !== 'resolved' && issue.status !== 'cancelled';

// Map an issue priority to a traffic light severity.
const issueSeverity = issue => {
  if (issue.priority === 'p1') return 'red';
  if (issue.priority === 'p2' || issue.priority === 'p3') return 'yellow';
  return 'blue';
};

const worstSeverity = severities =>
  severities.reduce((worst, s) => (SEVERITY_RANK[s] > SEVERITY_RANK[worst] ? s : worst), 'green');

export default function RoomStatusView() {
  const authCtx = useContext(AuthContext);
  const theme = useTheme();
  const navigate = useNavigate();
  const { getStatusOverview } = useIssue();

  const canRead =
    authCtx.checkRight('issue:read') ||
    authCtx.checkRight('issue:read:own') ||
    authCtx.checkRight('outage:read') ||
    authCtx.checkRight('outage:read:own');

  const [overview, setOverview] = useState(null);
  const [branchFilter, setBranchFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const res = await getStatusOverview();
        if (res) setOverview(res);
      } catch {
        // Error toast is emitted by the hook.
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [getStatusOverview]
  );

  useEffect(() => {
    if (!canRead) return undefined;
    load();
    const interval = setInterval(() => load(true), 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead]);

  const branches = useMemo(() => overview?.branches || [], [overview]);

  useEffect(() => {
    if (branches.length === 1) setBranchFilter(branches[0]._id);
  }, [branches]);

  const severityColor = severity => {
    if (severity === 'red') return theme.palette.error.main;
    if (severity === 'yellow') return theme.palette.warning.main;
    if (severity === 'blue') return theme.palette.info.main;
    return theme.palette.success.main;
  };

  // Full computed board: per branch → entertainment → resource with severity + tooltip info.
  const board = useMemo(() => {
    if (!overview) return [];
    const resourceById = Object.fromEntries((overview.resources || []).map(r => [r._id, r]));
    const openIssues = (overview.openIssues || []).filter(isOpenIssue);
    const entOutageByEnt = {};
    // A resource can belong to several entertainments; scope resource-level
    // outages to the entertainment they were reported on so a shared resource
    // is not marked out everywhere it appears.
    const resOutageByEntRes = {};
    (overview.activeOutages || []).forEach(o => {
      if (o.level === 'entertainment' && o.entertainmentId) entOutageByEnt[o.entertainmentId] = o;
      else if (o.resourceId && o.entertainmentId)
        resOutageByEntRes[`${o.entertainmentId}_${o.resourceId}`] = o;
    });

    // Build the tooltip lines and severity for a single unit.
    const unitInfo = (issues, outage, inheritedOutage) => {
      const severities = issues.map(issueSeverity);
      if (outage || inheritedOutage) severities.push('red');
      const lines = [];
      const effectiveOutage = outage || inheritedOutage;
      if (effectiveOutage) {
        lines.push(
          `Ausfall${effectiveOutage.reason ? `: ${effectiveOutage.reason}` : ''} (seit ${dayjs(
            effectiveOutage.startedAt
          ).format(DATE_FORMAT)})`
        );
      }
      issues.forEach(i => {
        const prio = ISSUE_PRIORITY_BY_KEY[i.priority]?.shortLabel || i.priority;
        lines.push(`${prio} · ${i.title}`);
      });
      return {
        severity: worstSeverity(severities),
        lines,
        hasOutage: Boolean(outage || inheritedOutage),
      };
    };

    // Worst status first so outages/red units surface at the top of the list.
    const byWorstThenName = (a, b) => {
      const d = (SEVERITY_RANK[b.severity] ?? 0) - (SEVERITY_RANK[a.severity] ?? 0);
      if (d) return d;
      return (a.entertainmentName || a.longName || '').localeCompare(
        b.entertainmentName || b.longName || ''
      );
    };

    return (overview.branches || [])
      .filter(b => branchFilter === 'all' || b._id === branchFilter)
      .map(branch => {
        const entertainments = (overview.entertainments || [])
          .filter(e => e.active !== false && e.branch === branch._id)
          .map(ent => {
            const entOutage = entOutageByEnt[ent._id];
            const entIssues = openIssues.filter(
              i => i.entertainmentId === ent._id && !i.resourceId
            );
            const resources = entertainmentResourceIds(ent)
              .map(id => resourceById[id])
              .filter(Boolean)
              .filter(r => r.active !== false)
              .map(res => {
                const resIssues = openIssues.filter(
                  i => i.resourceId === res._id && i.entertainmentId === ent._id
                );
                const info = unitInfo(
                  resIssues,
                  resOutageByEntRes[`${ent._id}_${res._id}`],
                  entOutage
                );
                return { ...res, ...info };
              });
            const ownInfo = unitInfo(entIssues, entOutage, null);
            const severity = worstSeverity([ownInfo.severity, ...resources.map(r => r.severity)]);
            const hasOutage = ownInfo.hasOutage || resources.some(r => r.hasOutage);
            return { ...ent, severity, hasOutage, lines: ownInfo.lines, resources };
          })
          .sort(byWorstThenName);
        const severity = worstSeverity(entertainments.map(e => e.severity));
        const hasOutage = entertainments.some(e => e.hasOutage);
        return { ...branch, entertainments, severity, hasOutage };
      })
      .filter(b => b.entertainments.length > 0)
      // Branches with the worst status first; alphabetical within the same level.
      .sort((a, b) => {
        const d = (SEVERITY_RANK[b.severity] ?? 0) - (SEVERITY_RANK[a.severity] ?? 0);
        if (d) return d;
        return branchLabel(a).localeCompare(branchLabel(b));
      });
  }, [overview, branchFilter]);

  // Counters across all displayed units (entertainments and their resources).
  const counts = useMemo(() => {
    const result = { red: 0, impaired: 0, ok: 0 };
    const countUnit = severity => {
      if (severity === 'red') result.red += 1;
      else if (severity === 'yellow' || severity === 'blue') result.impaired += 1;
      else result.ok += 1;
    };
    board.forEach(branch => {
      branch.entertainments.forEach(ent => {
        countUnit(ent.severity);
        ent.resources.forEach(res => countUnit(res.severity));
      });
    });
    return result;
  }, [board]);

  if (!canRead) return <Navigate to='/' replace />;

  const trafficDot = (severity, size) => (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        backgroundColor: severityColor(severity),
        ...(severity === 'red' && {
          animation: 'roomStatusPulse 1.6s ease-in-out infinite',
          '@keyframes roomStatusPulse': {
            '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0.55)}` },
            '70%': { boxShadow: `0 0 0 ${size / 2}px ${alpha(theme.palette.error.main, 0)}` },
            '100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0)}` },
          },
        }),
      }}
    />
  );

  const tooltipContent = (name, lines) => (
    <Box>
      <Typography variant='caption' fontWeight={700} display='block'>
        {name}
      </Typography>
      {lines.length === 0 ? (
        <Typography variant='caption'>Keine offenen Meldungen</Typography>
      ) : (
        lines.map((line, idx) => (
          <Typography key={idx} variant='caption' display='block'>
            {line}
          </Typography>
        ))
      )}
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, color: 'text.primary' }}>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        flexWrap='wrap'
        gap={2}
      >
        <Box>
          <Typography variant='h5' fontWeight={700}>
            Raum-Status
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Ampel-Übersicht über alle Entertainments und Räume.
          </Typography>
        </Box>
        <Stack direction='row' alignItems='center' spacing={1}>
          <FormControl size='small' sx={{ minWidth: 220 }}>
            <InputLabel id='room-status-branch-label'>Standort</InputLabel>
            <Select
              labelId='room-status-branch-label'
              label='Standort'
              value={branchFilter}
              onChange={e => setBranchFilter(e.target.value)}
            >
              <MenuItem value='all'>Alle Standorte</MenuItem>
              {branches.map(b => (
                <MenuItem key={b._id} value={b._id}>
                  {branchLabel(b)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title='Aktualisieren'>
            <IconButton onClick={() => load()} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {loading && !overview ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Stack direction='row' spacing={1} sx={{ mt: 2 }} flexWrap='wrap' useFlexGap>
            <Chip color='error' label={`${counts.red} nicht bespielbar`} />
            <Chip color='warning' label={`${counts.impaired} beeinträchtigt`} />
            <Chip color='success' label={`${counts.ok} ok`} />
          </Stack>

          {board.length === 0 && (
            <Alert severity='info' sx={{ mt: 3 }}>
              Keine Entertainments für den gewählten Standort gefunden.
            </Alert>
          )}

          {board.map(branch => (
            <Box key={branch._id} sx={{ mt: 3 }}>
              <Typography variant='subtitle1' fontWeight={700} sx={{ mb: 1 }}>
                {branchLabel(branch)}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: 2,
                }}
              >
                {branch.entertainments.map(ent => {
                  const isRed = ent.severity === 'red';
                  const isYellow = ent.severity === 'yellow';
                  return (
                  <Paper
                    key={ent._id}
                    variant='outlined'
                    onClick={() => navigate('/issues')}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      cursor: 'pointer',
                      borderWidth: isRed ? 2 : 1,
                      borderColor: isRed
                        ? theme.palette.error.main
                        : isYellow
                          ? theme.palette.warning.main
                          : theme.palette.divider,
                      backgroundColor: isRed
                        ? alpha(theme.palette.error.main, 0.1)
                        : isYellow
                          ? alpha(theme.palette.warning.main, 0.06)
                          : 'background.paper',
                      ...(isRed && {
                        boxShadow: `0 6px 22px ${alpha(theme.palette.error.main, 0.25)}`,
                      }),
                      '&:hover': {
                        backgroundColor: isRed
                          ? alpha(theme.palette.error.main, 0.16)
                          : isYellow
                            ? alpha(theme.palette.warning.main, 0.1)
                            : alpha(theme.palette.action.active, 0.04),
                      },
                    }}
                  >
                    <Tooltip
                      title={tooltipContent(ent.entertainmentName || ent.longName, ent.lines)}
                    >
                      <Stack
                        direction='row'
                        alignItems='center'
                        justifyContent='space-between'
                        spacing={1}
                      >
                        <Stack
                          direction='row'
                          alignItems='center'
                          spacing={1.5}
                          sx={{ minWidth: 0 }}
                        >
                          {trafficDot(ent.severity, 22)}
                          <Typography
                            variant='subtitle1'
                            fontWeight={700}
                            noWrap
                            sx={isRed ? { color: 'error.main' } : undefined}
                          >
                            {ent.entertainmentName || ent.longName}
                          </Typography>
                        </Stack>
                        {(isRed || isYellow) && (
                          <Chip
                            size='small'
                            color={isRed ? 'error' : 'warning'}
                            variant={isRed ? 'filled' : 'outlined'}
                            icon={<ReportProblemIcon />}
                            label={
                              isRed
                                ? ent.hasOutage
                                  ? 'Ausfall'
                                  : 'Nicht bespielbar'
                                : 'Beeinträchtigt'
                            }
                            sx={{ flexShrink: 0, fontWeight: 700 }}
                          />
                        )}
                      </Stack>
                    </Tooltip>
                    {ent.resources.length > 0 && (
                      <Stack
                        direction='row'
                        spacing={1}
                        sx={{ mt: 1.5 }}
                        flexWrap='wrap'
                        useFlexGap
                      >
                        {ent.resources.map(res => (
                          <Tooltip key={res._id} title={tooltipContent(res.name, res.lines)}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                px: 1,
                                py: 0.5,
                                borderRadius: 1.5,
                                border: `1px solid ${
                                  res.severity === 'red'
                                    ? theme.palette.error.main
                                    : theme.palette.divider
                                }`,
                                backgroundColor: alpha(
                                  severityColor(res.severity),
                                  res.severity === 'red' ? 0.18 : 0.08
                                ),
                              }}
                            >
                              {trafficDot(res.severity, 10)}
                              <Typography
                                variant='caption'
                                fontWeight={res.severity === 'red' ? 700 : 600}
                                sx={res.severity === 'red' ? { color: 'error.main' } : undefined}
                              >
                                {res.name}
                              </Typography>
                            </Box>
                          </Tooltip>
                        ))}
                      </Stack>
                    )}
                  </Paper>
                  );
                })}
              </Box>
            </Box>
          ))}
        </>
      )}
    </Box>
  );
}
